import { z } from "zod";
import { catalogueLocale, exerciseCategory, laterality, loadMode, muscleBodyRegion, muscleMovementType, videoGender, } from "./vocabulary";
/**
 * The exercise list — the wire shape of the list screen's only data source.
 *
 * ── THE PARAMETER NAMES LIVE HERE, ONCE ─────────────────────────────────────
 * `exerciseListQuery` parses them and {@link toExerciseListSearchParams} writes
 * them, and both read the same identifiers out of this file. A client that
 * spelled `muscle` where the server reads `muscleId` would not fail: the server
 * would ignore the unknown key and answer with the UNFILTERED list, which looks
 * like a filter that "does not work" rather than like an error. That is the
 * same silent-drift failure `foldForSearch` exists to prevent on the search
 * side, so it gets the same treatment — one definition, used by both ends.
 */
/** The default page size, and what the list screen requests. */
export const DEFAULT_EXERCISE_PAGE_SIZE = 30;
/**
 * The largest page anyone may ask for. D1 bills rows read (`T-6`), and a page
 * size is a multiplier on every join in the query behind it — so the cap is a
 * cost control, not a politeness.
 */
export const MAX_EXERCISE_PAGE_SIZE = 50;
/**
 * Query strings arrive as strings or not at all, so every field is decoded from
 * one. `z.coerce` is deliberately not used: it turns `""` into `0` and `"abc"`
 * into `NaN`, and a filter that silently becomes "muscle 0" returns an empty
 * list that reads as "no exercises work this muscle".
 */
const optionalId = z
    .string()
    .regex(/^\d+$/, "expected a positive integer id")
    .transform(Number)
    .pipe(z.int().positive())
    .optional();
export const exerciseListQuery = z.object({
    /**
     * Which language the localized text comes back in. Absent means English,
     * which is also the canonical column — the three-level fallback bottoms out
     * there for every exercise, so no request can produce a nameless row.
     */
    locale: catalogueLocale.optional(),
    /**
     * The raw search box input, accents and all. It is folded SERVER-side with
     * `foldForSearch`, so the client must not pre-fold it: folding twice is
     * harmless (the function is idempotent) but folding differently is the
     * failure, and the only way to be sure there is one folding is for there to
     * be one caller.
     */
    q: z.string().max(200).optional(),
    category: exerciseCategory.optional(),
    /**
     * Matches an exercise whose PRIMARY **or** SECONDARY equipment is this one.
     *
     * ── THE PROTOTYPE'S INCONSISTENCY, RESOLVED (T48) ─────────────────────────
     * The prototype counted secondary use and filtered on the primary column
     * only, so the two disagreed. Resolved in favour of including secondary, in
     * BOTH, for a reason that is visible on screen: `Head Harness` is primary
     * equipment for no exercise at all and secondary for seven. Under the
     * primary-only filter it appears in the filter list and returns nothing,
     * which reads as a broken filter. `/catalog/filters` counts the same way.
     */
    equipmentId: optionalId,
    /**
     * One specific muscle. Accepts `target` **or** `synergist` rows.
     *
     * The asymmetry with `region` and `movement` below is intentional and comes
     * from the discarded prototype: asking for "biceps" means "anything that
     * works my biceps", while asking for "upper body" or "pull" means "sessions
     * built around that", which is a target-role question.
     */
    muscleId: optionalId,
    /** Restricted to `target` rows. See the note on `muscleId`. */
    region: muscleBodyRegion.optional(),
    /** Restricted to `target` rows. See the note on `muscleId`. */
    movement: muscleMovementType.optional(),
    /**
     * Opaque. It is a keyset position, not an offset, and the client's only
     * correct use of it is to echo back the `nextCursor` it was handed. Anything
     * unparseable is rejected rather than ignored — a cursor silently treated as
     * "start from the beginning" makes an infinite-scroll list repeat its first
     * page forever.
     */
    cursor: z.string().max(200).optional(),
    limit: z
        .string()
        .regex(/^\d+$/, "expected a positive integer")
        .transform(Number)
        .pipe(z.int().min(1).max(MAX_EXERCISE_PAGE_SIZE))
        .optional(),
});
/**
 * The clip a card plays, or `null`.
 *
 * ── `null` IS A REAL, EXPECTED STATE — NOT AN ERROR AND NOT A SPINNER ───────
 * The catalogue's coverage is 3,000 of 3,001. `Sled Backward Angled Calf Raise`
 * has no clip because its only one was a mislabelled copy of a different
 * exercise's demonstration, and a training app that shows the wrong movement
 * teaches wrong form. The gap is recorded in `catalog_video_coverage_gap` with
 * its reason. A card that receives `null` renders its placeholder and stops;
 * treating it as "still loading" produces a card that spins forever.
 *
 * ── `width` AND `height` ARE WHY THIS OBJECT EXISTS AT ALL ──────────────────
 * There are 569 distinct clip resolutions. Without dimensions in the payload
 * the app cannot reserve layout space and every card in the list jumps as its
 * clip loads.
 *
 * `sizeBytes` is measured from the REMUXED file, never from the source
 * database's `file_size_bytes`, which describes the pre-remux bytes and is
 * wrong for all 3,791 clips.
 *
 * ── `thumbhash` IS NULLABLE, NOT OPTIONAL — SO THE API DEPLOYS FIRST ────────
 * `z.string().nullable()` accepts a hash and accepts `null`; it REJECTS
 * `undefined`. An API that already sends the field talks to an older app
 * without it, because the old app's schema ignores what it does not name — but
 * a new app against an older API fails validation in `readJson` on every card.
 * The deploy order is API, then app (`D121`; `T18` sequences it).
 */
export const exerciseClip = z.object({
    /** 64 lowercase hex characters. The R2 object key is `${hash}.mp4`. */
    hash: z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * The ThumbHash of the poster frame, base64 — about 19 bytes of it, decoded
     * by the app into the blur it paints while the poster loads. `null` where the
     * clip has no poster, which is a real state and renders the plain placeholder.
     * The poster itself is not carried here: its R2 key is `posters/${hash}.webp`,
     * derived from the clip `hash` above exactly as the clip key `${hash}.mp4` is.
     */
    thumbhash: z.string().nullable(),
    /**
     * Which demonstration this actually is. `male` is the canonical thumbnail,
     * but 555 exercises have only the female clip, so the card must be able to
     * say which one it is showing rather than assuming.
     */
    gender: videoGender,
    width: z.int().positive(),
    height: z.int().positive(),
    durationMs: z.int().positive(),
    sizeBytes: z.int().positive(),
});
/**
 * What a set of this exercise can be measured in (`D151`).
 *
 * ── THE SCHEMA'S OWN TERNARY, NOT A BOOLEAN ─────────────────────────────────
 * `catalog_exercise.tracks_*` hold `0` not tracked, `1` optional, `2` required
 * — the source database's convention, kept rather than flattened, because a
 * plank tracks duration at `2` and reps at `0` while a farmer's walk tracks
 * distance at `1`, and a boolean cannot say which field the set screen must
 * ask for and which it may. 743 of the 3,001 exercises track something other
 * than reps and weight; until this object travelled, none of them could be
 * logged.
 *
 * Declared here, beside `exerciseClip`, because the list row, the detail and
 * the plan item view all carry it and `training/plan.ts` already imports from
 * this file. The other direction would be a cycle.
 */
export const exerciseTracking = z.object({
    /** 0 not tracked · 1 optional · 2 required — the schema's own ternary. */
    distance: z.int().min(0).max(2),
    duration: z.int().min(0).max(2),
    reps: z.int().min(0).max(2),
    weight: z.int().min(0).max(2),
});
const namedRef = z.object({ id: z.int().positive(), name: z.string().min(1) });
/**
 * The R2 key of one of an exercise's still images (`D196`): 64 lowercase hex,
 * the SHA-256 of the file's bytes, under `cutouts/` or `frames/` as
 * `<hash>.webp`.
 *
 * Shaped exactly like a clip `hash`, and for the same reason: the key is the
 * content, so a re-cut image is a NEW key and never a new query string on an
 * old one — the object is served immutable for a year (`D121`). Declared once
 * so the list's `cutoutHash` and the detail's two image keys cannot drift
 * apart.
 */
export const exerciseImageHash = z.string().regex(/^[0-9a-f]{64}$/);
export const exerciseListItem = z.object({
    id: z.string().min(1),
    /**
     * Already resolved through the three-level fallback — localized text, then
     * English, then the canonical `name_en` column. The client never falls back
     * again, because it has nothing left to fall back to.
     */
    name: z.string().min(1),
    category: exerciseCategory,
    difficultyLevel: z.int().min(1).max(5),
    /**
     * The highest-activation muscle among the exercise's `target` rows. It is the
     * short label under the card, precomputed at ingestion — deriving it per row
     * on read would mean a grouped join over 9,001 rows for a screenful (`T-6`).
     */
    primaryMuscle: namedRef,
    equipment: namedRef,
    clip: exerciseClip.nullable(),
    /**
     * Optional in the CONTRACT only: the server always sends it once `T11`
     * lands, and an app updated before that deploy must still parse the list.
     * A row without it is read as *"reps and weight"*, the shape every row had
     * until `D151`.
     */
    tracks: exerciseTracking.optional(),
    /**
     * How a logged load reads (`D196`) — see {@link loadMode}. On the CARD
     * because the picker and the session's set screen are fed from list rows
     * and the device's snapshot, and both need to say "kg por halter" before
     * anyone opens the sheet.
     *
     * ── NULLABLE AND OPTIONAL, AND THE TWO MEAN DIFFERENT THINGS ──────────────
     * `null` is a server that knows the column and has no classification for
     * this row; absent is a server from before `0027`. Both read as `total`.
     * Optional because a new output field stays optional (`D172`): an app
     * updated before the API deploys must still parse every list page.
     */
    loadMode: loadMode.nullable().optional(),
    /**
     * The exercise's figure on a transparent background (`D196`), the R2 key
     * `cutouts/<hash>.webp` — what a card or a chip can draw without loading a
     * clip. `null` where the second layer has no cut-out for this exercise,
     * which is an ordinary state: the card keeps its poster. Optional for the
     * deploy window, as above (`D172`).
     */
    cutoutHash: exerciseImageHash.nullable().optional(),
    /**
     * Whether the sides work in turn (`D232`) — see {@link laterality}. On the
     * card, and so in the device's catalogue copy, for `loadMode`'s reason: a
     * swapped exercise is logged per side from the copy. Sent only where
     * known, which keeps the copy small; absent is unknown, read as before.
     */
    laterality: laterality.nullable().optional(),
});
export const exerciseListOutput = z.object({
    items: z.array(exerciseListItem),
    /**
     * `null` means this was the last page. A client that keeps requesting after
     * a `null` gets the first page back, which is why the end is stated rather
     * than inferred from a short page — a page can be short and still have more
     * behind it once a filter is involved.
     */
    nextCursor: z.string().nullable(),
});
/**
 * The one place a catalogue query becomes wire parameters.
 *
 * ── WHY IT RETURNS A PLAIN RECORD AND NOT `URLSearchParams` ────────────────
 * This package compiles with `lib: ["ES2022"]` and no DOM, because it is
 * imported by a Worker, by an Expo app and by three test runners, and adding a
 * platform's type declarations to a wire contract would be claiming a runtime
 * it has no business knowing about. A record is what every one of those can
 * consume, and it is also exactly the shape the server parses — `c.req.query()`
 * in Hono returns one — so the round trip in the tests is the real round trip.
 *
 * Undefined fields are OMITTED rather than serialized as `"undefined"`, which
 * is what `URLSearchParams.set` does with an undefined value and which would
 * reach the server as a literal string that fails the enum parse.
 */
export function toExerciseListParams(query) {
    const params = {};
    const put = (key, value) => {
        if (value !== undefined && value !== "") {
            params[key] = String(value);
        }
    };
    put("locale", query.locale);
    put("q", query.q);
    put("category", query.category);
    put("equipmentId", query.equipmentId);
    put("muscleId", query.muscleId);
    put("region", query.region);
    put("movement", query.movement);
    put("cursor", query.cursor);
    put("limit", query.limit);
    return params;
}
/**
 * The same parameters as a query string, `?` included, or `""` when there are
 * none.
 *
 * Empty rather than a bare `?`: some routers treat `/catalog/exercises?` as a
 * different path from `/catalog/exercises`, and a 404 on the second one reads
 * as a missing route rather than as a stray character.
 *
 * `encodeURIComponent` on both halves. The search term is the one that matters —
 * it is arbitrary user input and routinely contains a space or an accent, and
 * an unencoded `Flexão de Braço` is a malformed URL rather than a search that
 * quietly finds nothing.
 */
export function toExerciseListQueryString(query) {
    const pairs = Object.entries(toExerciseListParams(query)).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    return pairs.length === 0 ? "" : `?${pairs.join("&")}`;
}
//# sourceMappingURL=exercise-list.js.map